import React, { Fragment, useState } from 'react'
import moment from 'moment'
import { object, bool, func, string, array } from 'prop-types'
import ConfigConsumer from '../../redux/configuration/context'
import SvgIcon from '../SvgIcon/SvgIcon'
import { capitalizeFirstLetter } from '../../configuration/utilities'
import dateFormats from '../../configuration/dateFormats'
import { useSelector } from 'react-redux'
import { selectDepartureRoute } from '../../redux/ferryRouteSelections/selectors'

export const getDateValue = (
  departureTime,
  departureDate,
  labels,
  language
) => {
  moment.locale(language)

  const date = moment(departureDate)

  let departingOnFormatted = date.format(
    language === 'fr'
      ? dateFormats.summaryTableLongDisplayFrench
      : dateFormats.summaryTableLongDisplay
  )

  if (language === 'fr')
    departingOnFormatted = capitalizeFirstLetter(departingOnFormatted)

  const departureTimeString = !!departureTime
    ? `${labels.at} ${departureTime}`
    : null

  return `${departingOnFormatted}${
    !!departureTime ? ` ${departureTimeString}` : ''
  }`
}

const KennelQuantitiesCard = ({
  labels,
  content,
  kennels,
  selections,
  accommodationsContent,
  startingFrom,
  onConfirmClick,
  crossing,
  direction,
  header,
  lang
}) => {
  const departureRoute = useSelector(selectDepartureRoute)
  const ferryId = departureRoute.ferryId
  const kennelsDetails = Object.values(kennels).map(x => x.details)
  const initialState = []
  if (selections?.length > 0) {
    Object.values(selections).forEach(selection => {
      for (let selIdx = 0; selIdx < selection[direction]; selIdx++) {
        initialState.push(selection.resourceCode)
      }
    })
  } else {
    initialState.push('inVehicle')
  }
  const [selectedQuantities, setSelectedQuantities] = useState(initialState)

  const availKennelSpace = kennel => {
    const quantity =
      selections.find(s => s.resourceCode === kennel.resourceCode)?.[
        direction
      ] || 0
    return kennel?.freeCapacity - quantity
  }

  const insertAtIndex = (array, index, newItem) => {
    if (index === 0) {
      return [newItem, ...array]
    }
    if (array.length <= index) {
      return [...array, newItem]
    }
    return [...array.slice(0, index), newItem, ...array.slice(index)]
  }

  const getSelectOptions = () => {
    let options = kennelsDetails
      .map(details => {
        const key = details.resourceCode
        if (!kennels?.[key]?.[direction]) {
          return null
        }
        if (ferryId === 'nfl' && key === 'PTE') {
          return null
        }
        if (
          ferryId === 'bfl' &&
          key === 'PTL'
          // || (ferryId === 'bfl' && key === 'PTE')
        ) {
          return null
        }
        const kennelContent = accommodationsContent.accommodations[key]
        const freeCapacity = kennels[key][direction]
          ? availKennelSpace(kennels[key][direction])
          : 0
        return (
          <option key={key} value={key} disabled={freeCapacity <= 0}>
            {kennelContent?.title || labels[key] || key}
          </option>
        )
      })
      .filter(e => !!e)

    options = insertAtIndex(
      options,
      0,
      <option key="_" value="inVehicle">
        {`${
          labels.inVehicle || ferryId === 'bfl'
            ? 'In Vehicle'
            : 'In Vehicle/On-Leash: Exterior Decks Only'
        }`}
      </option>
    )

    return <Fragment>{options}</Fragment>
  }

  const updateSelect = (index, newCode) => {
    const newState = [...selectedQuantities]
    newState[index] = newCode
    setSelectedQuantities(newState)
    handleConfirmClick(newState)
  }

  const removeRow = index => {
    const newState = [...selectedQuantities]
    newState.splice(index, 1)
    setSelectedQuantities(newState)
    handleConfirmClick(newState)
  }

  const canAddKennelRow = true // check
  const addKennelRow = () => {
    const newState = [...selectedQuantities, '']
    setSelectedQuantities(newState)
    handleConfirmClick(newState)
  }

  const handleConfirmClick = updateState => {
    const selectionObj = updateState.reduce(
      (acc, resourceCode) => {
        if (
          resourceCode === '' ||
          resourceCode === 'inVehicle' ||
          resourceCode === 'onLeash'
        )
          return acc

        if (acc?.[direction]?.[resourceCode]) {
          acc[direction][resourceCode].count++
        } else {
          const details = kennelsDetails.find(
            x => x.resourceCode === resourceCode
          )
          acc[direction][resourceCode] = {
            count: 1,
            details
          }
        }
        return acc
      },
      {
        [direction]: {}
      }
    )
    onConfirmClick(selectionObj)
  }

  let priceElement = null
  if (startingFrom) {
    priceElement = (
      <div className="extras__card-start pad">
        <div className="extras__card-start-label">{labels.startingFrom}</div>
        <div className="extras__card-start-value">{startingFrom}</div>
        <div className="extras__card-start-label">{labels.perKennel}</div>
      </div>
    )
  }

  const quantitySelectGrid = (crossing, header) => (
    <div className="extras__card-kennel-grid">
      <div className="extras__card-kennel-meta">
        <h5>{header}</h5>
        <div className="extras__card-kennel-meta__date">
          <div className="extras__card-kennel-meta__date-upper">
            <ConfigConsumer>
              {({ themeVariables }) => (
                <SvgIcon
                  className="select-value-icon extras__card-kennel-meta__date-upper-icon"
                  type="calendar"
                  fill={themeVariables.themeIconColor}
                />
              )}
            </ConfigConsumer>
            <span className="extras__card-kennel-meta__date-upper-label">
              {labels.date}
            </span>
          </div>
          <hr />
          <span>
            {getDateValue(
              crossing.departureTime,
              crossing.departureDate,
              labels,
              lang
            )}
          </span>
        </div>
      </div>
      <div className="extras__card-kennel-amounts">
        <div className="extras__card-grid-label">
          {labels.petTravelOptions || 'Pet Travel Options'}{' '}
          <span className="extras__card-grid-label-ship-name">
            {labels[crossing.shipCode]}
          </span>
        </div>
        {selectedQuantities.map((resourceCode, index) => {
          return (
            <div
              key={`sel_${index}`}
              className="extras__card-kennel-amounts__options"
            >
              <select
                className="custom-dropdown"
                value={resourceCode}
                onChange={e => {
                  updateSelect(index, e.target.value)
                }}
              >
                {getSelectOptions()}
              </select>
              {index !== 0 && (
                <button
                  className="extras__card-kennel-amounts__options__remove"
                  onClick={e => removeRow(index)}
                >
                  X
                </button>
              )}
            </div>
          )
        })}
        <div className="extras__card-grid-label add-btn">
          {canAddKennelRow && (
            <span onClick={addKennelRow}>
              {labels.extraKennel || 'Extra Kennel +'}
            </span>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="extras__card">
      {content && (
        <div
          className="extras__card-description"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
      <div className="extras__card-content">
        <div className="kennel-content">
          <div className="extras__card-bottom">{priceElement}</div>
          {quantitySelectGrid(crossing, header)}
        </div>
      </div>
    </div>
  )
}

KennelQuantitiesCard.defaultProps = {
  content: null
}

KennelQuantitiesCard.propTypes = {
  labels: object.isRequired,
  content: string,
  kennels: object.isRequired,
  selections: array,
  accommodationsContent: object.isRequired,
  confirmButtonText: string.isRequired,
  closeButtonText: string.isRequired,
  isOneWay: bool.isRequired,
  onConfirmClick: func.isRequired,
  crossing: object.isRequired,
  direction: string.isRequired,
  header: string.isRequired,
  lang: string.isRequired
}

export default KennelQuantitiesCard
